// services/api/reports.api.js - HR Reports API Service
// Following the existing API service pattern in the project

import apiClient from './axios.config';
import { API_ENDPOINTS } from '../../constants';

/**
 * HR Reports API Service
 * Provides methods for fetching HR-related reports and analytics data
 * Following MVC pattern with proper separation of concerns
 */
export const reportsAPI = {
  /**
   * Get HR Reports Overview Data
   * @param {Object} filters - Filter parameters for the report
   * @returns {Promise} API response with HR metrics data
   */
  getHRReports: (filters = {}) => {
    const params = {
      department: filters.department || '',
      dateFrom: filters.dateFrom || '',
      dateTo: filters.dateTo || '',
      employeeType: filters.employeeType || '',
      status: filters.status || '',
      reportType: 'hr_overview'
    };
    return apiClient.get(API_ENDPOINTS.REPORTS_HR, { params });
  },

  /**
   * Get Employee Metrics Data
   * @param {Object} filters - Filter parameters
   * @returns {Promise} API response with employee metrics
   */
  getEmployeeMetrics: (filters = {}) => {
    const params = {
      ...filters,
      reportType: 'employee_metrics'
    };
    return apiClient.get(API_ENDPOINTS.REPORTS_HR, { params });
  },

  /**
   * Get Attendance Reports Data
   * @param {Object} filters - Filter parameters
   * @returns {Promise} API response with attendance data
   */
  getAttendanceReports: (filters = {}) => {
    const params = {
      ...filters,
      reportType: 'attendance'
    };
    return apiClient.get(API_ENDPOINTS.REPORTS_ATTENDANCE, { params });
  },

  /**
   * Get Leave Analytics Data
   * @param {Object} filters - Filter parameters
   * @returns {Promise} API response with leave analytics
   */
  getLeaveAnalytics: (filters = {}) => {
    const params = {
      ...filters,
      reportType: 'leave_analytics'
    };
    return apiClient.get(API_ENDPOINTS.REPORTS_HR, { params });
  },

  /**
   * Get Performance Metrics Data
   * @param {Object} filters - Filter parameters
   * @returns {Promise} API response with performance data
   */
  getPerformanceMetrics: (filters = {}) => {
    const params = {
      ...filters,
      reportType: 'performance'
    };
    return apiClient.get(API_ENDPOINTS.REPORTS_HR, { params });
  },

  /**
   * Get Department Analysis Data
   * @param {Object} filters - Filter parameters
   * @returns {Promise} API response with department statistics
   */
  getDepartmentAnalysis: (filters = {}) => {
    const params = {
      ...filters,
      reportType: 'department_analysis'
    };
    return apiClient.get(API_ENDPOINTS.REPORTS_HR, { params });
  },

  /**
   * Export HR Report
   * @param {string} format - Export format (pdf, excel, csv)
   * @param {Object} filters - Filter parameters
   * @param {string} reportType - Type of report to export
   * @returns {Promise} API response with file download
   */
  exportHRReport: (format, filters = {}, reportType = 'hr_overview') => {
    const params = {
      ...filters,
      format,
      reportType
    };
    return apiClient.get(API_ENDPOINTS.REPORTS_HR + '/export', { 
      params,
      responseType: 'blob' // Important for file downloads
    });
  },

  /**
   * Get Finance Reports Data
   * @param {Object} filters - Filter parameters
   * @returns {Promise} API response with finance reports
   */
  getFinanceReports: (filters = {}) => {
    const params = {
      ...filters,
      reportType: 'finance_overview'
    };
    return apiClient.get(API_ENDPOINTS.REPORTS_FINANCE, { params });
  },

  /**
   * Get Payroll Reports Data
   * @param {Object} filters - Filter parameters
   * @returns {Promise} API response with payroll reports
   */
  getPayrollReports: (filters = {}) => {
    const params = {
      ...filters,
      reportType: 'payroll_overview'
    };
    return apiClient.get(API_ENDPOINTS.REPORTS_PAYROLL, { params });
  },

  /**
   * Get Procurement Reports Data
   * @param {Object} filters - Filter parameters
   * @returns {Promise} API response with procurement reports
   */
  getProcurementReports: (filters = {}) => {
    const params = {
      ...filters,
      reportType: 'procurement_overview'
    };
    return apiClient.get(API_ENDPOINTS.REPORTS_PROCUREMENT, { params });
  },

  /**
   * Get Budget Reports Data
   * @param {Object} filters - Filter parameters
   * @returns {Promise} API response with budget reports
   */
  getBudgetReports: (filters = {}) => {
    const params = {
      ...filters,
      reportType: 'budget_overview'
    };
    return apiClient.get(API_ENDPOINTS.REPORTS_BUDGET, { params });
  },

  /**
   * Get Asset Reports Data
   * @param {Object} filters - Filter parameters
   * @returns {Promise} API response with asset reports
   */
  getAssetReports: (filters = {}) => {
    const params = {
      ...filters,
      reportType: 'asset_overview'
    };
    return apiClient.get(API_ENDPOINTS.REPORTS_ASSETS, { params });
  }
};

export default reportsAPI;
